// Processors taken from next-seo main branch
// https://github.com/garmeeh/next-seo/blob/main/src/utils/processors.ts

// Schema.org type constants
const SCHEMA_TYPES = {
  PERSON: 'Person',
  ORGANIZATION: 'Organization',
  IMAGE_OBJECT: 'ImageObject',
  POSTAL_ADDRESS: 'PostalAddress',
  CONTACT_POINT: 'ContactPoint',
  QUANTITATIVE_VALUE: 'QuantitativeValue',
  GEO_COORDINATES: 'GeoCoordinates',
  GEO_SHAPE: 'GeoShape',
  OPENING_HOURS: 'OpeningHoursSpecification',
  REVIEW: 'Review',
  RATING: 'Rating',
  AGGREGATE_RATING: 'AggregateRating',
  MERCHANT_RETURN_POLICY: 'MerchantReturnPolicy',
  MERCHANT_RETURN_POLICY_SEASONAL_OVERRIDE: 'MerchantReturnPolicySeasonalOverride',
  MONETARY_AMOUNT: 'MonetaryAmount',
  VIDEO_OBJECT: 'VideoObject',
  INTERACTION_COUNTER: 'InteractionCounter',
  BRAND: 'Brand',
  CREDIT_CARD: 'CreditCard',
  UNIT_PRICE_SPECIFICATION: 'UnitPriceSpecification',
  MEMBER_PROGRAM: 'MemberProgram',
  MEMBER_PROGRAM_TIER: 'MemberProgramTier',
  BED_DETAILS: 'BedDetails',
  LOCATION_FEATURE: 'LocationFeatureSpecification',
  ACCOMMODATION: 'Accommodation',
  PLACE: 'Place',
  PERFORMING_GROUP: 'PerformingGroup',
  OFFER: 'Offer',
  AGGREGATE_OFFER: 'AggregateOffer',
  PRICE_SPECIFICATION: 'PriceSpecification',
  ITEM_LIST: 'ItemList',
  LIST_ITEM: 'ListItem',
  PRODUCT: 'Product',
  PRODUCT_GROUP: 'ProductGroup',
  NUTRITION_INFORMATION: 'NutritionInformation',
  HOW_TO_STEP: 'HowToStep',
  HOW_TO_SECTION: 'HowToSection',
  HOW_TO_SUPPLY: 'HowToSupply',
  HOW_TO_TOOL: 'HowToTool',
  HOW_TO_DIRECTION: 'HowToDirection',
  HOW_TO_TIP: 'HowToTip',
  PROPERTY_VALUE: 'PropertyValue',
  CREATIVE_WORK: 'CreativeWork',
  DATA_DOWNLOAD: 'DataDownload',
  DATA_CATALOG: 'DataCatalog',
  COUNTRY: 'Country',
  STATE: 'State',
  EDUCATIONAL_CREDENTIAL: 'EducationalOccupationalCredential',
  OCCUPATIONAL_EXPERIENCE: 'OccupationalExperienceRequirements',
  COMMENT: 'Comment',
  WEB_PAGE: 'WebPage',
  WEB_PAGE_ELEMENT: 'WebPageElement',
  CLAIM: 'Claim',
  CERTIFICATION: 'Certification',
  PEOPLE_AUDIENCE: 'PeopleAudience',
  SIZE_SPECIFICATION: 'SizeSpecification',
  THREE_D_MODEL: '3DModel',
  DEFINED_REGION: 'DefinedRegion',
  SHIPPING_DELIVERY_TIME: 'ShippingDeliveryTime',
  OFFER_SHIPPING_DETAILS: 'OfferShippingDetails'
};

// Type guard utilities
function hasType(obj) {
  return obj !== null && typeof obj === 'object' && '@type' in obj;
}

function isString(value) {
  return typeof value === 'string';
}

// Generic processor for simple schema types
export function processSchemaType(value, schemaType, stringHandler, numberHandler) {
  if (isString(value) && stringHandler) {
    return { '@type': schemaType, ...stringHandler(value) };
  }

  if (typeof value === 'number' && numberHandler) {
    return { '@type': schemaType, ...numberHandler(value) };
  }

  if (hasType(value)) {
    return value;
  }

  // Ensure value is an object before spreading
  if (typeof value === 'object' && value !== null) {
    return { '@type': schemaType, ...value };
  }

  // Fallback for non-object values
  return { '@type': schemaType };
}

/**
 * Processes image input into ImageObject schema type
 * @param image - URL string or ImageObject
 * @returns URL string or ImageObject with @type
 * @example
 * processImage("https://example.com/image.jpg") // "https://example.com/image.jpg"
 * processImage({ url: "image.jpg", width: 800 }) // { "@type": "ImageObject", ... }
 */
export function processImage(image) {
  if (isString(image)) {
    return image;
  }

  return processSchemaType(image, SCHEMA_TYPES.IMAGE_OBJECT);
}

/**
 * Processes address input into PostalAddress schema type
 * @param address - String address or PostalAddress object
 * @returns PostalAddress with @type
 * @example
 * processAddress("123 Main St") // { "@type": "PostalAddress", streetAddress: "123 Main St" }
 */
export function processAddress(address) {
  return processSchemaType(
    address,
    SCHEMA_TYPES.POSTAL_ADDRESS,
    str => ({ streetAddress: str }),
    undefined
  );
}

// JobPosting-specific processors

/**
 * Processes hiring organization into Organization schema type
 * @param org - String name or Organization object
 * @returns Organization with @type and processed logo
 */
export function processHiringOrganization(org) {
  if (isString(org)) {
    return {
      '@type': SCHEMA_TYPES.ORGANIZATION,
      name: org
    };
  }

  const processed = processSchemaType(org, SCHEMA_TYPES.ORGANIZATION);

  // Process nested logo if present
  if (processed.logo && !isString(processed.logo)) {
    processed.logo = processImage(processed.logo);
  }

  return processed;
}

/**
 * Processes job location into Place schema type
 * @param location - String location or JobPlace object
 * @returns JobPlace with @type and processed address
 */
export function processJobLocation(location) {
  if (isString(location)) {
    return {
      '@type': SCHEMA_TYPES.PLACE,
      address: {
        '@type': SCHEMA_TYPES.POSTAL_ADDRESS,
        streetAddress: location
      }
    };
  }

  const processed = processSchemaType(location, SCHEMA_TYPES.PLACE);

  // Process nested address
  if (processed.address && !isString(processed.address)) {
    processed.address = processAddress(processed.address);
  }

  return processed;
}

/**
 * Processes monetary amount into MonetaryAmount schema type
 * @param amount - MonetaryAmount object with or without @type
 * @returns MonetaryAmount with @type and processed value
 */
export function processMonetaryAmount(amount) {
  const processed = processSchemaType(amount, 'MonetaryAmount');

  // Process nested value as QuantitativeValue
  processed.value = processSchemaType(amount.value, SCHEMA_TYPES.QUANTITATIVE_VALUE);

  return processed;
}

/**
 * Processes job property value into PropertyValue schema type
 * @param identifier - String identifier or JobPropertyValue object
 * @returns JobPropertyValue with @type
 */
export function processJobPropertyValue(identifier) {
  return processSchemaType(
    identifier,
    SCHEMA_TYPES.PROPERTY_VALUE,
    str => ({ value: str }),
    undefined
  );
}

/**
 * Processes applicant location requirements into Country or State schema type
 * @param location - Location requirement object
 * @returns AdministrativeArea (Country or State) with @type
 */
export function processApplicantLocationRequirements(location) {
  if (hasType(location)) {
    return location;
  }

  // Improved detection logic
  const name = location.name;
  const statePatterns = [
    /\b[A-Z]{2}\b/, // Two-letter state codes
    /\bstate\b/i, // Contains "state"
    /,/, // Contains comma (often "City, State")
    /\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)\b/ // US state codes
  ];

  const isState = statePatterns.some(pattern => pattern.test(name));

  return {
    '@type': isState ? SCHEMA_TYPES.STATE : SCHEMA_TYPES.COUNTRY,
    ...location
  };
}

/**
 * Processes education requirements into string or EducationalOccupationalCredential
 * @param education - String description or credential object
 * @returns String or EducationalOccupationalCredential with @type
 */
export function processEducationRequirements(education) {
  if (isString(education)) {
    return education;
  }

  return processSchemaType(education, SCHEMA_TYPES.EDUCATIONAL_CREDENTIAL);
}

/**
 * Processes experience requirements into string or OccupationalExperienceRequirements
 * @param experience - String description or experience object
 * @returns String or OccupationalExperienceRequirements with @type
 */
export function processExperienceRequirements(experience) {
  if (isString(experience)) {
    return experience;
  }

  return processSchemaType(experience, SCHEMA_TYPES.OCCUPATIONAL_EXPERIENCE);
}
